/* eslint-disable no-lonely-if */
import { ICitizenLessonDeviation, ICitizenLessonDeviationErrors, ICourseState, ICourseStateErrors, ILesson, ILessonErrors, IOfferPeriod } from './interfaces';
import { toMinutes } from './utils';
import { combineDateAndTime, formatDate, formatTimeHHMM, toStartOfDay } from '../../utilities';

export function validateLesson(lesson: ILesson, offerPeriod?: IOfferPeriod): [boolean, ILessonErrors] {
  const tmpErrors = {} as ILessonErrors;
  let isValid = true;

  const {
    startTime,
    endTime,
    attendanceCheckStart,
    attendanceCheckEnd,
    earliestArrivalOnTimeBeforeStart,
    latestArrivalOnTimeAfterStart,
    latestAttendanceRegistrationAfterStart,
    earliestDepartureRegistrationBeforeEnd,
    earliestDepartureOnTimeBeforeEnd,
    latestDepartureOnTimeAfterEnd,
    isReoccuringLesson,
    reoccuringLessonStartDate,
    reoccuringLessonEndDate,
    startDate,

    weekdays,
  } = lesson;

  if (!startTime) {
    tmpErrors.startTime = 'Starttidspunkt skal angives';
    isValid = false;
  }
  if (!endTime) {
    tmpErrors.endTime = 'Sluttidspunkt skal angives';
    isValid = false;
  }

  if(startTime && endTime && typeof startTime !== 'string' && typeof endTime !== 'string') {
    const startInMinutes = toMinutes(startTime);
    const endInMinutes = toMinutes(endTime);

    if(startInMinutes >= endInMinutes) {
      tmpErrors.endTime = 'Sluttidspunkt skal være efter starttidspunkt';
      isValid = false;
    }
  }

  if (attendanceCheckStart) {
    if (!earliestArrivalOnTimeBeforeStart) {
      tmpErrors.earliestArrivalOnTimeBeforeStart = 'Tidligst til tiden skal angives';
      isValid = false;
    }
    if (!latestArrivalOnTimeAfterStart) {
      tmpErrors.latestArrivalOnTimeAfterStart = 'Senest til tiden skal angives';
      isValid = false;
    }
    if (!latestAttendanceRegistrationAfterStart) {
      tmpErrors.latestAttendanceRegistrationAfterStart = 'Seneste registrering af fremmøde skal angives';
      isValid = false;
    }
  }

  if (attendanceCheckEnd) {
    if (!earliestDepartureRegistrationBeforeEnd) {
      tmpErrors.earliestDepartureRegistrationBeforeEnd = 'Tidligste registrering af afgang skal angives';
      isValid = false;
    }

    if (!earliestDepartureOnTimeBeforeEnd) {
      tmpErrors.earliestDepartureOnTimeBeforeEnd = 'Tidligst til tiden skal angives';
      isValid = false;
    }
    if (!latestDepartureOnTimeAfterEnd) {
      tmpErrors.latestDepartureOnTimeAfterEnd = 'Senest til tiden skal angives';
      isValid = false;
    }
  }

  if (isReoccuringLesson) {
    if (!reoccuringLessonStartDate) {
      tmpErrors.reoccuringLessonStartDate = 'Startdato skal angives';
      isValid = false;
    }
    if (!reoccuringLessonEndDate) {
      tmpErrors.reoccuringLessonEndDate = 'Slutdato skal angives';
      isValid = false;
    }

    if (reoccuringLessonEndDate && reoccuringLessonStartDate && new Date(reoccuringLessonEndDate) < new Date(reoccuringLessonStartDate)) {
      tmpErrors.reoccuringLessonEndDate = 'Slutdato skal være efter eller lig med startdato';
      isValid = false;
    }
  } else {
    if (!startDate) {
      tmpErrors.startDate = 'Startdato skal angives';
      isValid = false;
    }
  }

  if (isReoccuringLesson) {
    if (!weekdays || weekdays.length === 0) {
      tmpErrors.weekdays = 'Minimum 1 gentagelses dag skal angives';
      isValid = false;
    }
  }

  const offerStart = toStartOfDay(offerPeriod?.startDate);
  const offerEnd = toStartOfDay(offerPeriod?.endDate);
  const beforeOfferStartError = `Lektionen må ikke ligge før tilbuddets startdato (${formatDate(offerPeriod?.startDate)})`;
  const afterOfferEndError = `Lektionen må ikke ligge efter tilbuddets slutdato (${formatDate(offerPeriod?.endDate)})`;

  if (!isReoccuringLesson) {
    const lessonDate = toStartOfDay(startDate);
    if (offerStart && lessonDate && lessonDate < offerStart) {
      tmpErrors.startDate = beforeOfferStartError;
      isValid = false;
    } else if (offerEnd && lessonDate && lessonDate > offerEnd) {
      tmpErrors.startDate = afterOfferEndError;
      isValid = false;
    }
  } else {
    const rangeStart = toStartOfDay(reoccuringLessonStartDate);
    const rangeEnd = toStartOfDay(reoccuringLessonEndDate);
    if (offerStart && rangeStart && rangeStart < offerStart) {
      tmpErrors.reoccuringLessonStartDate = beforeOfferStartError;
      isValid = false;
    }
    if (offerEnd && rangeEnd && rangeEnd > offerEnd) {
      tmpErrors.reoccuringLessonEndDate = afterOfferEndError;
      isValid = false;
    }
  }

  return [isValid, tmpErrors];
}

export function validateCitizenLessonDeviation(deviation: ICitizenLessonDeviation): [boolean, ICitizenLessonDeviationErrors] {
  const tmpErrors = {} as ICitizenLessonDeviationErrors;
  let isValid = true;

  const { startDate, startTime, endTime, isExemptFromLesson, minTime, maxTime } = deviation;

  if (!isExemptFromLesson) {
    if (!startDate) {
      tmpErrors.startDate = 'Dato skal angives';
      isValid = false;
    }
    if (!startTime) {
      tmpErrors.startTime = 'Starttidspunkt skal angives';
      isValid = false;
    }
    if (!endTime) {
      tmpErrors.endTime = 'Sluttidspunkt skal angives';
      isValid = false;
    }

    const start = combineDateAndTime(startDate, startTime);
    const end = combineDateAndTime(startDate, endTime);

    if (start && start < minTime) {
      tmpErrors.startTime = `Starttidspunkt skal være efter lektionens starttidspunkt (${formatTimeHHMM({ hours: minTime.getHours(), minutes: minTime.getMinutes() })})`;
      isValid = false;
    }
    if (end && end > maxTime) {
      tmpErrors.endTime = `Sluttidspunkt skal være før lektionens sluttidspunkt (${formatTimeHHMM({ hours: maxTime.getHours(), minutes: maxTime.getMinutes() })})`;
      isValid = false;
    }
    if (start && end && end <= start) {
      tmpErrors.endTime = 'Sluttidspunkt skal være efter starttidspunkt';
      isValid = false;
    }
  }

  return [isValid, tmpErrors];
}

export function validateCourse(course: ICourseState): [boolean, ICourseStateErrors] {
  const tmpErrors = {} as ICourseStateErrors;
  let isValid = true;
  if (!course.name) {
    tmpErrors.name = 'Titel skal angives';
    isValid = false;
  }
  if (!course.companyId) {
    tmpErrors.companyId = 'Virksomhed skal vælges';
    isValid = false;
  }

  if (course.hasLimitedSeats) {
    if (!course.capacity) {
      tmpErrors.capacity = 'Antal pladser skal angives';
      isValid = false;
    } else if (course.capacity < 0) {
      tmpErrors.capacity = 'Antal pladser skal være et positivt tal';
      isValid = false;
    } else if (course.offerCapacity != null && course.capacity > course.offerCapacity) {
      tmpErrors.capacity = `Antal pladser på holdet må ikke overstige tilbuddets antal pladser (${course.offerCapacity}).`;
      isValid = false;
    }
  }

  if (course.attendanceCheckStart) {
    if (course.earliestArrivalOnTimeBeforeStart === undefined) {
      tmpErrors.earliestArrivalOnTimeBeforeStart = 'Tidligst til tiden skal angives';
      isValid = false;
    }
    if (course.latestArrivalOnTimeAfterStart === undefined) {
      tmpErrors.latestArrivalOnTimeAfterStart = 'Senest til tiden skal angives';
      isValid = false;
    }
    if (course.latestAttendanceRegistrationAfterStart === undefined) {
      tmpErrors.latestAttendanceRegistrationAfterStart = 'Seneste registrering af fremmøde skal angives';
      isValid = false;
    }
  }

  if (course.attendanceCheckEnd) {
    if (course.earliestDepartureRegistrationBeforeEnd === undefined) {
      tmpErrors.earliestDepartureRegistrationBeforeEnd = 'Tidligste registrering af afgang skal angives';
      isValid = false;
    }
    if (course.earliestDepartureOnTimeBeforeEnd === undefined) {
      tmpErrors.earliestDepartureOnTimeBeforeEnd = 'Tidligst til tiden skal angives';
      isValid = false;
    }
    if (course.latestDepartureOnTimeAfterEnd === undefined) {
      tmpErrors.latestDepartureOnTimeAfterEnd = 'Senest til tiden skal angives';
      isValid = false;
    }
  }

  if (!course.desiredReportType) {
    tmpErrors.desiredReportType = 'Frekvens for fremmøderapporter skal vælges';
    isValid = false;
  }

  return [isValid, tmpErrors];
}
